import React from 'react';
import { Link } from 'react-router-dom';
import { formatCurrency } from '../../../utils/currencyUtils';
import StatusBadge from '../../ui/StatusBadge';

const RecentActivityItem = ({ claim }) => {
  const formatClaimType = (type) => {
    switch (type) {
      case 'DEATH':
        return 'Death Claim';
      case 'DISABILITY':
        return 'Disability Claim';
      case 'CRITICAL_ILLNESS':
        return 'Critical Illness';
      default:
        return type?.replace('_', ' ') || 'Unknown';
    }
  };

  const getClaimTypeIcon = (type) => {
    switch (type) {
      case 'DEATH':
        return (
          <svg className="w-5 h-5 text-crystal-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'DISABILITY':
        return (
          <svg className="w-5 h-5 text-crystal-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
        );
      case 'CRITICAL_ILLNESS':
        return (
          <svg className="w-5 h-5 text-crystal-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
          </svg>
        );
      default:
        return (
          <svg className="w-5 h-5 text-crystal-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
          </svg>
        );
    }
  };

  const formatSubmissionDate = (dateString) => {
    if (!dateString) return 'Unknown date';
    
    const date = new Date(dateString);
    const now = new Date();
    const diffInDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) {
      return 'Today';
    } else if (diffInDays === 1) {
      return 'Yesterday';
    } else if (diffInDays <= 7) {
      return `${diffInDays} days ago`;
    } else {
      return date.toLocaleDateString('en-GB', {
        day: 'numeric',
        month: 'short',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
      });
    }
  };


  return (
    <div 
      className="flex items-start justify-between p-4 border-b border-silver-mist last:border-b-0 hover:bg-silver-mist/30 transition-all duration-200 group"
      data-testid="recent-activity-item"
    >
      <div className="flex items-start space-x-4 flex-1">
        {/* Claim Type Icon */}
        <div className="flex-shrink-0">
          <div className="h-12 w-12 bg-gradient-to-br from-deep-jungle to-forest-fern flex items-center justify-center shadow-sm group-hover:shadow-md transition-shadow duration-200" style={{ borderRadius: '12px' }}>
            {getClaimTypeIcon(claim.type)}
          </div>
        </div>
        
        {/* Claim Details */}
        <div className="flex-1 min-w-0">
          {/* Primary Info */}
          <div className="flex items-center space-x-3 mb-2">
            <h4 
              className="text-sm font-bold text-deep-jungle leading-tight group-hover:text-forest-fern transition-colors duration-200"
              data-testid="claim-id"
            >
              <span data-testid="claim-type">{formatClaimType(claim.type)}</span> #{claim.claimId || claim.id}
            </h4>
            <div data-testid="claim-status">
              <StatusBadge status={claim.action || claim.status} size="small" />
            </div>
          </div>
          
          {/* Amount - Highlighted */}
          <div className="mb-2">
            <span 
              className="text-lg font-bold text-fresh-leaf group-hover:text-forest-fern transition-colors duration-200"
              data-testid="claim-amount"
            >
              {formatCurrency(claim.approvedBenefitAmount || claim.benefitAmount)}
            </span>
          </div>
          
          {/* Secondary Info */}
          <div className="space-y-1">
            <p className="text-xs text-steel leading-relaxed">
              <span className="font-medium text-graphite">Claimant:</span> 
              <span data-testid="claimant-name"> {claim.claimantName || 'Not specified'}</span>
            </p>
            <p className="text-xs text-steel leading-relaxed">
              <span 
                className="font-medium text-forest-fern"
                data-testid="submission-date"
              >
                {formatSubmissionDate(claim.submittedAt || claim.timestamp)}
              </span>
            </p>
          </div>
        </div>
      </div>
      
      {/* Action Button */}
      <div className="flex items-center ml-4 flex-shrink-0">
        <Link
          to={`/claims/${claim.claimId || claim.id}`}
          className="inline-flex items-center space-x-2 text-xs font-semibold text-fresh-leaf hover:text-forest-fern border border-fresh-leaf/30 hover:border-forest-fern/40 hover:bg-fresh-leaf/5 px-4 py-2 transition-all duration-200 focus-visible group-hover:shadow-sm"
          style={{ borderRadius: '8px' }}
          title={`View details for ${formatClaimType(claim.type)} #${claim.claimId || claim.id}`}
          data-testid="view-claim-button"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
          </svg>
          <span>View Details</span>
        </Link>
      </div>
    </div>
  );
};

export default RecentActivityItem;
